/**
 * Primary Axios instance for all Corevian ERP API calls.
 *
 * Loading integration:
 *   All requests go through the LoadingManager.
 *   The loading type is controlled via request config:
 *
 *     api.get('/url')                          → 'progress' (default)
 *     api.get('/url', { _loader: 'silent' })   → no visual indicator
 *     api.get('/url', { _loader: 'background'})→ silent
 *     api.get('/url', { _loader: 'fullscreen'})→ full-screen AppLoader
 *
 *   This allows per-call control without touching interceptor logic.
 *
 * Auth integration:
 *   JWT token is attached from localStorage.
 *   401 responses trigger auto-logout (unless it's the login endpoint).
 */

import axios from 'axios';
import store from '@/store';
import LoadingManager from '@/services/loadingManager';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 10000,
});

// ─────────────────────────────────────────────────────────────────────────────
// Request interceptor
// ─────────────────────────────────────────────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    // Attach JWT token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Start loading counter for the requested type
    const loaderType = config._loader || 'progress';
    if (loaderType !== 'none') {
      LoadingManager.start(loaderType);
      // Store the type on the config so the response handler decrements the right counter
      config._loaderType = loaderType;
    }

    return config;
  },
  (error) => {
    // Request setup failed — decrement if we already incremented
    const loaderType = error.config?._loaderType;
    if (loaderType && loaderType !== 'none') {
      LoadingManager.end(loaderType);
    }
    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Response interceptor
// ─────────────────────────────────────────────────────────────────────────────

api.interceptors.response.use(
  (response) => {
    // Decrement the correct loading type
    const loaderType = response.config?._loaderType;
    if (loaderType && loaderType !== 'none') {
      LoadingManager.end(loaderType);
    }
    return response;
  },
  (error) => {
    // Always decrement on error to prevent stuck loaders
    const loaderType = error.config?._loaderType;
    if (loaderType && loaderType !== 'none') {
      LoadingManager.end(loaderType);
    }

    // Handle 401 — auto-logout (skip for login endpoint)
    if (
      error.response &&
      error.response.status === 401 &&
      !error.config?.url?.includes('/auth/login')
    ) {
      store.dispatch('auth/logout');
    }

    return Promise.reject(error);
  },
);

export default api;
